import { debug } from './utils';

export type TrafficOrigin = 'owner' | 'public';
export type AnalyticsConsent = 'accepted' | 'rejected';

const CONSENT_KEY = 'doganju.analytics.consent';
declare const __DOGANJU_GA4_MEASUREMENT_ID__: string | undefined;
declare const __DOGANJU_ANALYTICS_OWNER_UID__: string | undefined;
declare const __DOGANJU_ANALYTICS_ENABLED__: string | undefined;

const MEASUREMENT_ID = (typeof __DOGANJU_GA4_MEASUREMENT_ID__ !== 'undefined' && __DOGANJU_GA4_MEASUREMENT_ID__) || 'G-99ZXWJ1GX8';
const OWNER_UID = (typeof __DOGANJU_ANALYTICS_OWNER_UID__ !== 'undefined' && __DOGANJU_ANALYTICS_OWNER_UID__) || 'eM8LskRoYofoMISqVdznbh6LO483';
const PRODUCTION_HOSTS = new Set(['doganju.com', 'www.doganju.com']);

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let scriptPromise: Promise<void> | null = null;
let configured = false;
let trafficOrigin: TrafficOrigin = 'public';

const isCollectionAllowed = () => {
  const override = typeof __DOGANJU_ANALYTICS_ENABLED__ !== 'undefined' ? __DOGANJU_ANALYTICS_ENABLED__ : undefined;
  if (override === 'true') return true;
  if (override === 'false') return false;
  return PRODUCTION_HOSTS.has(window.location.hostname);
};

const getStoredConsent = (): AnalyticsConsent | null => {
  const value = window.localStorage.getItem(CONSENT_KEY);
  return value === 'accepted' || value === 'rejected' ? value : null;
};

export const getAnalyticsConsent = (): AnalyticsConsent | null => getStoredConsent();

export const setAnalyticsConsent = (consent: AnalyticsConsent) => {
  const wasAccepted = getStoredConsent() === 'accepted';
  window.localStorage.setItem(CONSENT_KEY, consent);
  window.dispatchEvent(new CustomEvent('doganju:analytics-consent', { detail: consent }));
  if (consent === 'accepted') void initializeAnalytics().catch(logAnalyticsError);
  else {
    withdrawAnalytics();
    if (wasAccepted) window.location.reload();
  }
};

const loadScript = async () => {
  if (!scriptPromise && window.gtag) return;
  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function (..._args: unknown[]) {
        // eslint-disable-next-line prefer-rest-params -- Google tag requires Arguments objects, not arrays.
        window.dataLayer.push(arguments);
      };
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Google Analytics script failed to load'));
      document.head.appendChild(script);
    });
  }
  await scriptPromise;
};

export const setTrafficOrigin = (uid: string | null | undefined) => {
  trafficOrigin = uid && uid === OWNER_UID ? 'owner' : 'public';
  if (configured) window.gtag?.('set', { traffic_origin: trafficOrigin });
  return trafficOrigin;
};

export const initializeAnalytics = async () => {
  if (configured || getStoredConsent() !== 'accepted' || !isCollectionAllowed()) return;
  if (!MEASUREMENT_ID || !/^G-[A-Z0-9]+$/i.test(MEASUREMENT_ID)) throw new Error('Invalid VITE_GA4_MEASUREMENT_ID');
  await loadScript();
  if (configured || getStoredConsent() !== 'accepted') return;
  window.gtag?.('js', new Date());
  window.gtag?.('config', MEASUREMENT_ID, { send_page_view: false });
  window.gtag?.('set', { traffic_origin: trafficOrigin });
  configured = true;
};

const sanitizeUrl = (value: string) => {
  const url = new URL(value, window.location.origin);
  url.search = '';
  url.hash = '';
  return url.href;
};

export const trackPageView = async (pagePath: string, pageTitle: string, referrer?: string) => {
  if (getStoredConsent() !== 'accepted' || !isCollectionAllowed()) return false;
  await initializeAnalytics();
  if (!configured || getStoredConsent() !== 'accepted') return false;
  window.gtag?.('event', 'page_view', {
    page_title: pageTitle,
    page_location: sanitizeUrl(`${window.location.origin}${pagePath}`),
    page_referrer: referrer ? sanitizeUrl(referrer) : document.referrer ? sanitizeUrl(document.referrer) : undefined,
    traffic_origin: trafficOrigin,
  });
  return true;
};

export const withdrawAnalytics = () => {
  configured = false;
  scriptPromise = null;
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0]?.trim();
    if (name?.startsWith('_ga')) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}`;
      document.cookie = `${name}=; Max-Age=0; path=/`;
    }
  });
};

export const isAnalyticsCollectionAllowed = isCollectionAllowed;
export const getAnalyticsOwnerUid = () => OWNER_UID;
export const logAnalyticsError = (error: unknown) => debug('Analytics error:', error);
